import type { FormApi } from 'final-form';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import useRequest from '@swf/core/hooks/useRequest';
import type { Dispatch } from '@swf/core/store';
import type { PageData, PokerContext } from '@swf/core/types/Poker';
import { PokerModalRoutes } from '@swf/core/types/Poker';
import type {
  CreateClubTableRequest,
  CreateClubTableResponse,
  HGCreateClubTableReply,
  TableParams
} from '@swf/generated/types/api/v0/SwHomeGames';
import { handleServerResponse } from '@swf/poker';

import type { FormValues } from '../utils/common';
import { getMatchingGameObject } from '../utils/common';

interface State {
  error?: string | null;
  message?: string | null;
  data: HGCreateClubTableReply;
}

interface LocationState extends PageData {
  clubId: number;
  fetchService: PokerContext['fetchService'];
}

const initialResponse: State = { error: null, message: null, data: {} };

const useCreateClubTable = (data: TableParams[]) => {
  const request = useRequest();
  const [response, setResponse] = useState(initialResponse);
  const [loading, setLoading] = useState(false);
  const { modal } = useDispatch<Dispatch>();
  const {
    state: { clubs, clubId, fetchService }
  } = useLocation<LocationState>();

  const handleSubmit = (values: FormValues, form: FormApi<FormValues, Partial<FormValues>>) => {
    const tableParams = getMatchingGameObject(form.getState().values, data);

    if (!tableParams) return;

    setLoading(true);

    request({
      service: 'homeGames.createClubTableRequest',
      body: {
        createClubTable: {
          clubId,
          tableParams: {
            ...tableParams,
            tableName: values.tableName
          },
          videoChatEnabled: form.getState().values.videoChatSupported
        }
      } as CreateClubTableRequest
    }).subscribe({
      next: ({ body }: { body: CreateClubTableResponse }) => {
        const { error, data, message } = handleServerResponse<
          CreateClubTableResponse,
          HGCreateClubTableReply
        >({
          response: body,
          accessor: 'createClubTableReply'
        });

        setResponse({ error, data, message });
        setLoading(false);

        if (!error) {
          modal.replace([
            {
              path: PokerModalRoutes.createCashGameSuccess,
              locationState: { clubs, fetchService }
            }
          ]);
        }
      }
    });
  };

  return { handleSubmit, response, loading };
};

export default useCreateClubTable;
